import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "./AccountManagement.css";
import HeaderMain from "../../../components/Header/HeaderMain";
import Footer from "../../../components/Footer/Footer";
import NewsLetter from "../../../components/NewsLetter/NewsLetter";
import {
  FaUserShield,
  FaClipboardCheck,
  FaPaintBrush,
  FaChartLine,
  FaBullhorn,
  FaBoxes,
} from "react-icons/fa";
import { marketplaceList, companyName, contact } from "./accountData";
import {
  SectionHead,
  WhyChooseUs,
  StatsBand,
  FinalCta,
  MarketplaceIcon,
} from "./AccountParts";

// "What we do across every marketplace" — the hub-level services overview.
const HUB_SERVICES = [
  {
    icon: FaUserShield,
    title: "Account Setup & Onboarding",
    desc: "Correct registration, document submission, brand and category approvals and dashboard configuration on every marketplace you sell on.",
  },
  {
    icon: FaClipboardCheck,
    title: "Listing & Catalogue Management",
    desc: "Keyword-rich, fully compliant listings and catalogues built to each platform's standards for maximum discoverability.",
  },
  {
    icon: FaPaintBrush,
    title: "Storefront & Brand Design",
    desc: "Professional brand stores and storefronts that tell your story and turn browsers into loyal, repeat buyers.",
  },
  {
    icon: FaBullhorn,
    title: "Advertising & Promotions",
    desc: "Sponsored ads, deals and seasonal campaigns managed for maximum ROI and the lowest possible advertising cost of sale.",
  },
  {
    icon: FaChartLine,
    title: "Growth & Optimization",
    desc: "Continuous tuning of price, content and conversion rate, aligned with each marketplace's ranking algorithm.",
  },
  {
    icon: FaBoxes,
    title: "Inventory, Payments & Support",
    desc: "Stock control, payment reconciliation, returns handling and real-time support that keeps your account healthy.",
  },
];

// How-it-works process steps.
const PROCESS = [
  {
    title: "Audit & Strategy",
    desc: "We start with a free, in-depth audit of your account and category, then build a clear, data-driven growth plan.",
  },
  {
    title: "Setup & Optimization",
    desc: "We fix and optimize your listings, catalogue, storefront and pricing so your foundation is rank-ready.",
  },
  {
    title: "Advertise & Promote",
    desc: "We launch and manage sponsored ads, deals and campaigns to drive qualified, high-intent traffic.",
  },
  {
    title: "Scale & Report",
    desc: "We monitor performance daily, manage inventory and payments, and report transparently as you scale.",
  },
];

const AccountManagement = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="am-page">
      <HeaderMain />

      {/* Hero */}
      <section className="am-hero am-wrap">
        <div className="am-hero-inner">
          <div className="am-hero-text" data-aos="fade-right">
            <span className="am-eyebrow">Seller Account Management</span>
            <h1>
              Seller <span>Account Management</span> Services
            </h1>
            <p className="am-tagline">
              One expert team to launch, manage and scale your store across
              India's and the world's biggest marketplaces — Amazon, Flipkart,
              Meesho, JioMart, Etsy, eBay, Walmart and Alibaba.
            </p>
            <p className="am-lead">
              {companyName} offers complete, end-to-end seller account
              management — expert product listing and cataloguing, result-driven
              advertising, inventory control, payment reconciliation and full
              compliance. Whether you are a brand-new seller or scaling an
              established store, our tailored solutions boost product visibility,
              increase conversions and help you grow faster and smarter.
            </p>
            <div className="am-cta-row">
              <a className="am-btn am-btn-primary" href={contact.phoneTel}>
                Get Started
              </a>
              <Link className="am-btn am-btn-outline" to="/services">
                All Services
              </Link>
            </div>
          </div>
          <div className="am-hero-art" data-aos="fade-left">
            <div className="am-hero-badge">
              <div className="am-hero-icon">
                <FaChartLine />
              </div>
              <h3>End-to-End Handling</h3>
              <p>
                From onboarding to advertising, inventory and support — we run
                the whole account so you can focus on your product.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="am-section am-wrap">
        <SectionHead
          eyebrow="Overview"
          title="Your Strategic Marketplace Partner"
        />
        <div className="am-richtext" data-aos="fade-up">
          <p>
            In today's competitive market, where hundreds of sellers offer the
            same products, visibility is the single biggest differentiator.
            Being found in searches on Amazon, Flipkart and Google is what
            separates a store that grows from one that stalls — and that is
            exactly where expert account management and cataloguing make all the
            difference.
          </p>
          <p>
            {companyName} is a leading multi-marketplace account management
            provider. Choosing us means gaining more than a service vendor; you
            gain a strategic partner committed to your growth, navigating the
            complexities of every online marketplace alongside you. Join the
            businesses that trust us to manage and grow their e-commerce
            presence.
          </p>
        </div>
      </section>

      {/* Marketplaces we manage */}
      <section className="am-section am-wrap">
        <SectionHead
          eyebrow="Marketplaces We Manage"
          title="Pick Your Marketplace"
          intro="We are platform-trained experts on every major Indian and international marketplace. Choose one to see exactly how we set up, optimize and scale your account there."
        />
        <div className="am-grid am-grid-4">
          {marketplaceList.map((m) => (
            <Link
              className="am-mp-card"
              to={`/services/account-management/${m.slug}`}
              data-aos="zoom-in"
              key={m.slug}
            >
              <span className="am-mp-icon">
                <MarketplaceIcon icon={m.icon} />
              </span>
              <h4>{m.name}</h4>
              <p>{m.name} seller account management</p>
              <span className="am-mp-link">Learn more →</span>
            </Link>
          ))}
        </div>
      </section>

      {/* What we do */}
      <section className="am-section am-wrap">
        <SectionHead
          eyebrow="What We Do"
          title="Complete Account Management Services"
          intro="A single, accountable team covering every part of selling online — on every channel you sell on."
        />
        <div className="am-grid am-grid-3">
          {HUB_SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <div className="am-card" data-aos="zoom-in" key={s.title}>
                <div className="am-card-icon">
                  <Icon />
                </div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Stats band */}
      <StatsBand />

      {/* Process */}
      <section className="am-section am-wrap">
        <SectionHead
          eyebrow="How It Works"
          title="A Simple, Proven Process"
          intro="From the first free audit to month-on-month scaling, here is exactly how we grow your account."
        />
        <div className="am-grid am-grid-4">
          {PROCESS.map((step, i) => (
            <div className="am-step" data-aos="zoom-in" key={step.title}>
              <div className="am-step-num">{i + 1}</div>
              <h4>{step.title}</h4>
              <p>{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why choose us */}
      <WhyChooseUs />

      {/* Final CTA */}
      <FinalCta />

      <NewsLetter />
      <Footer />
    </div>
  );
};

export default AccountManagement;
